import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import usePeriodChange from '@/hooks/usePeriodChange';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SearchableSelect } from '@/components/SearchableSelect';
import { Plus, ShoppingCart, Trash2, Building2, Pencil, ArrowRightLeft, Package } from 'lucide-react';
import { toast } from 'sonner';

// Helper functions defined outside component
const createEmptyRow = () => ({
  name: '',
  weight_kg: '',
  price_per_kg: '',
  subtotal: '',
  notes: '',
  keterangan: '' // For badge display like "Stok Awal"
});

const createDefaultRows = () => {
  return Array(5).fill(null).map(() => createEmptyRow());
};

const Purchases = ({ user, branchId, onDataChange }) => {
  const [purchases, setPurchases] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    branch_id: branchId || '',
    branch_account_id: '',
    supplier_name: '',
    purchase_date: new Date().toISOString().split('T')[0],
    partai: '',
    items: createDefaultRows()
  });
  const [editingId, setEditingId] = useState(null);
  const [branchAccounts, setBranchAccounts] = useState([]);
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [selectedPurchases, setSelectedPurchases] = useState([]);
  const [bulkPartai, setBulkPartai] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // New states for move and convert features
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [activePeriods, setActivePeriods] = useState([]);
  const [isClosedPeriod, setIsClosedPeriod] = useState(false);
  const [hasClosedPeriod, setHasClosedPeriod] = useState(false);
  const [editingConverted, setEditingConverted] = useState(false);
  const [selectedTargetPeriod, setSelectedTargetPeriod] = useState('');
  const [convertedPurchases, setConvertedPurchases] = useState([]);
  const [normalPurchases, setNormalPurchases] = useState([]);

  useEffect(() => {
    if (branchId) {
      setFormData(prev => ({ ...prev, branch_id: branchId }));
    }
  }, [branchId]);

  useEffect(() => {
    fetchData();
    fetchActivePeriods();
  }, [branchId]);

  usePeriodChange(() => fetchData());

  useEffect(() => {
    if (branchId) {
      fetchBranchAccounts();
    }
  }, [branchId]);

  const fetchBranchAccounts = async () => {
    try {
      const branchRes = await api.get(`/branches/${branchId}`);
      setBranchAccounts(branchRes.data.accounts || []);
    } catch (error) {
      console.error('Failed to fetch branch accounts:', error);
    }
  };

  const fetchData = async () => {
    try {
      // Get selected period from localStorage
      const periodId = localStorage.getItem('selectedPeriodId');
      
      // Build query params
      const params = periodId ? { period_id: periodId } : {};
      
      // OPTIMIZED: Parallel API calls for better performance
      const apiCalls = [
        api.get('/purchases', { params }),
        api.get('/branches'),
        api.get('/periods/active').catch(() => null) // Catch error for active period
      ];
      
      // Only fetch period transfers if we have a periodId
      if (periodId) {
        apiCalls.push(
          api.get('/period-transfers', { params: { source_period_id: periodId } }).catch(() => ({ data: [] }))
        );
      }
      
      const results = await Promise.all(apiCalls);
      const [purchasesRes, branchesRes, activePeriodRes, transfersRes] = results;
      
      // Filter purchases by branchId if provided
      const filteredPurchases = branchId 
        ? purchasesRes.data.filter(p => p.branch_id === branchId)
        : purchasesRes.data;
      
      // Identify converted purchases from period transfers
      let convertedPurchaseIds = [];
      if (transfersRes) {
        const convertTransfers = transfersRes.data.filter(t => t.transfer_type === 'PURCHASE_TO_STOCK');
        convertedPurchaseIds = convertTransfers.flatMap(t => t.entity_ids || []);
      }
      
      // Separate purchases into converted and normal
      const converted = filteredPurchases.filter(p => convertedPurchaseIds.includes(p.id));
      const normal = filteredPurchases.filter(p => !convertedPurchaseIds.includes(p.id));
      
      setPurchases(filteredPurchases);
      setConvertedPurchases(converted);
      setNormalPurchases(normal);
      setBranches(branchesRes.data);
      
      if (activePeriodRes) {
        setActivePeriod(activePeriodRes.data);
      }
      
      // Notify parent component to refresh its data (for card totals)
      if (onDataChange) {
        onDataChange();
      }
    } catch (error) {
      toast.error('Gagal memuat data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRow = () => {
    // Add 3 rows at once
    setFormData({
      ...formData,
      items: [...formData.items, createEmptyRow(), createEmptyRow(), createEmptyRow()]
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formData.items];
    updatedItems[index][field] = value;

    // Auto-calculate subtotal when weight or price changes (if subtotal is not manually set)
    if (field === 'weight_kg' || field === 'price_per_kg') {
      const weight = parseFormattedNumber(updatedItems[index].weight_kg);
      const price = parseFormattedNumber(updatedItems[index].price_per_kg);
      
      // Only auto-calculate if both weight and price have values
      if (weight > 0 && price > 0) {
        updatedItems[index].subtotal = formatNumber(weight * price);
      }
    }

    setFormData({
      ...formData,
      items: updatedItems
    });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData({ ...formData, items: updatedItems });
  };

  const handleKeyDown = (e, rowIndex, colIndex) => {
    // Prevent form submission on Enter
    if (e.key === 'Enter') {
      e.preventDefault();
      
      // Move down to next row, same column
      const nextRowIndex = rowIndex + 1;
      const inputId = `item-${nextRowIndex}-${colIndex}`;
      const nextInput = document.getElementById(inputId);
      
      if (nextInput) {
        nextInput.focus();
      } else if (nextRowIndex >= formData.items.length) {
        // Add new row if at the end
        const newItem = createEmptyRow();
        setFormData({
          ...formData,
          items: [...formData.items, newItem]
        });
        
        // Focus will be set after state update
        setTimeout(() => {
          const newInputId = `item-${nextRowIndex}-${colIndex}`;
          const newInput = document.getElementById(newInputId);
          if (newInput) newInput.focus();
        }, 50);
      }
    }
    // Tab key default behavior is already correct (moves to next input)
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!activePeriod) {
      toast.error('Tidak ada periode aktif. Silakan buat periode terlebih dahulu.');
      return;
    }

    // Validate rekening is selected
    if (!formData.branch_account_id) {
      toast.error('Rekening wajib dipilih');
      return;
    }

    // Validate rekening exists in branchAccounts (prevent saving with invalid account)
    const selectedAccount = branchAccounts.find(acc => acc.id === formData.branch_account_id);
    if (!selectedAccount) {
      toast.error('Rekening yang dipilih tidak valid. Silakan pilih rekening yang tersedia.');
      return;
    }

    // Filter out empty rows
    const filledItems = formData.items.filter(item => 
      item.name || parseFormattedNumber(item.weight_kg) > 0 || 
      parseFormattedNumber(item.price_per_kg) > 0 || parseFormattedNumber(item.subtotal) > 0
    );

    if (filledItems.length === 0) {
      toast.error('Tambahkan minimal 1 bahan');
      return;
    }

    setLoading(true);

    try {
      // Parse formatted numbers to float
      const itemsForSubmit = filledItems.map(item => ({
        id: item.id, // Include item ID for backend matching (if editing)
        name: item.name || '',
        weight_kg: parseFormattedNumber(item.weight_kg),
        price_per_kg: parseFormattedNumber(item.price_per_kg),
        subtotal: parseFormattedNumber(item.subtotal),
        notes: item.notes || '',
        keterangan: item.keterangan || '' // PRESERVE keterangan (important for "Stok Awal" badge)
      }));

      // Get selected period from localStorage
      const selectedPeriodId = localStorage.getItem('selectedPeriodId');
      
      const payload = {
        branch_id: branchId,
        branch_account_id: formData.branch_account_id,  // Already validated above
        supplier_name: formData.supplier_name,
        purchase_date: new Date(formData.purchase_date).toISOString(),
        partai: formData.partai || '',
        items: itemsForSubmit,
        period_id: selectedPeriodId || null  // Send selected period to backend
      };

      if (editingId) {
        await api.put(`/purchases/${editingId}`, payload);
        toast.success('Pembelian berhasil diperbarui');
      } else {
        await api.post('/purchases', payload);
        toast.success('Pembelian berhasil ditambahkan');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operasi gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (purchase) => {
    setEditingId(purchase.id);
    setEditingConverted(!!purchase.source_purchase_id);
    setFormData({
      branch_id: purchase.branch_id,
      branch_account_id: purchase.branch_account_id || '',
      supplier_name: purchase.supplier_name,
      purchase_date: new Date(purchase.purchase_date).toISOString().split('T')[0],
      partai: purchase.partai || '',
      items: purchase.items.map(item => ({
        id: item.id, // Preserve item ID for backend matching
        name: item.name,
        weight_kg: formatNumber(item.weight_kg),
        price_per_kg: formatNumber(item.price_per_kg),
        subtotal: formatNumber(item.subtotal),
        notes: item.notes || '',
        keterangan: item.keterangan || '' // PRESERVE keterangan (important for "Stok Awal" badge)
      }))
    });
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    console.log('Delete clicked for purchase:', id);
    setDeletingId(id);
    setDeleteDialogOpen(true);
    console.log('Delete dialog should open now');
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;

    setDeleting(true);
    try {
      await api.delete(`/purchases/${deletingId}`);
      toast.success('Pembelian berhasil dihapus');
      
      // Close dialogs and reset state
      setDeleteDialogOpen(false);
      setDialogOpen(false);
      setDeletingId(null);
      resetForm();
      
      // Refresh data
      await fetchData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Gagal menghapus pembelian');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkEditPartai = async () => {
    if (selectedPurchases.length === 0) {
      toast.error('Pilih minimal 1 pembelian');
      return;
    }

    if (!bulkPartai.trim()) {
      toast.error('Partai tidak boleh kosong');
      return;
    }

    setLoading(true);
    try {
      // Update each selected purchase with new partai
      await Promise.all(
        selectedPurchases.map(async (purchaseId) => {
          const purchase = purchases.find(p => p.id === purchaseId);
          if (purchase) {
            const payload = {
              branch_id: purchase.branch_id,
              branch_account_id: purchase.branch_account_id,  // PRESERVE account (bulk edit must not empty it)
              supplier_name: purchase.supplier_name,
              purchase_date: new Date(purchase.purchase_date).toISOString(),
              partai: bulkPartai,
              items: purchase.items.map(item => ({
                id: item.id, // Preserve item ID
                name: item.name,
                weight_kg: item.weight_kg,
                price_per_kg: item.price_per_kg,
                subtotal: item.subtotal,
                notes: item.notes || '',
                keterangan: item.keterangan || '' // PRESERVE keterangan
              }))
            };
            await api.put(`/purchases/${purchaseId}`, payload);
          }
        })
      );
      
      toast.success(`${selectedPurchases.length} pembelian berhasil diperbarui`);
      setSelectedPurchases([]);
      setBulkPartai('');
      setBulkEditDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Gagal memperbarui partai');
    } finally {
      setLoading(false);
    }
  };

  const toggleSelectPurchase = (purchaseId) => {
    setSelectedPurchases(prev => 
      prev.includes(purchaseId) 
        ? prev.filter(id => id !== purchaseId)
        : [...prev, purchaseId]
    );
  };

  const togglePartaiPurchases = (ids) => {
    const allSelected = ids.every(id => selectedPurchases.includes(id));
    setSelectedPurchases(prev => allSelected
      ? prev.filter(id => !ids.includes(id))
      : [...new Set([...prev, ...ids])]
    );
  };

  const toggleSelectAll = () => {
    if (selectedPurchases.length === purchases.length) {
      setSelectedPurchases([]);
    } else {
      setSelectedPurchases(purchases.map(p => p.id));
    }
  };

  const fetchActivePeriods = async () => {
    try {
      const response = await api.get('/periods');
      const currentPeriodId = localStorage.getItem('selectedPeriodId');
      
      // Get current period to compare dates
      const currentPeriod = response.data.find(p => p.id === currentPeriodId);
      setIsClosedPeriod(!!currentPeriod?.is_closed);
      setHasClosedPeriod(response.data.some(p => p.is_closed));
      
      if (!currentPeriod) {
        setActivePeriods([]);
        return;
      }
      
      const currentStartDate = new Date(currentPeriod.start_date);
      
      // Filter: only non-closed periods with start_date AFTER current period (future periods)
      const futurePeriods = response.data.filter(p => {
        if (p.is_closed || p.id === currentPeriodId) return false;
        const periodStartDate = new Date(p.start_date);
        return periodStartDate > currentStartDate; // Only future periods
      });
      
      setActivePeriods(futurePeriods);
    } catch (error) {
      console.error('Failed to fetch active periods:', error);
    }
  };

  const handleMoveToPeriod = async () => {
    if (!selectedTargetPeriod) {
      toast.error('Pilih periode tujuan terlebih dahulu');
      return;
    }

    // Validate all selected purchases are from the same branch
    const selectedPurchaseItems = purchases.filter(p => selectedPurchases.includes(p.id));
    const branches = [...new Set(selectedPurchaseItems.map(p => p.branch_id))];
    if (branches.length > 1) {
      toast.error('Hanya bisa memindahkan pembelian dari cabang yang sama');
      return;
    }

    setLoading(true);
    try {
      await api.post('/purchases/move-to-period', {
        purchase_ids: selectedPurchases,
        target_period_id: selectedTargetPeriod
      });
      
      toast.success(`${selectedPurchases.length} pembelian berhasil dipindahkan ke periode lain`);
      setMoveDialogOpen(false);
      setSelectedPurchases([]);
      setSelectedTargetPeriod('');
      
      // Refresh data - don't let fetchData errors bubble up
      try {
        await fetchData();
      } catch (fetchError) {
        console.error('Failed to refresh data after move:', fetchError);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal memindahkan pembelian');
    } finally {
      setLoading(false);
    }
  };

  const handleConvertToStock = async () => {
    if (!selectedTargetPeriod) {
      toast.error('Pilih periode tujuan terlebih dahulu');
      return;
    }

    // Validate all selected purchases are from the same branch
    const selectedPurchaseItems = purchases.filter(p => selectedPurchases.includes(p.id));
    const branches = [...new Set(selectedPurchaseItems.map(p => p.branch_id))];
    if (branches.length > 1) {
      toast.error('Hanya bisa mengkonversi pembelian dari cabang yang sama');
      return;
    }

    setLoading(true);
    try {
      await api.post('/purchases/convert-to-stock', {
        purchase_ids: selectedPurchases,
        target_period_id: selectedTargetPeriod
      });
      
      toast.success(`${selectedPurchases.length} pembelian berhasil dijadikan stok di periode lain`);
      setConvertDialogOpen(false);
      setSelectedPurchases([]);
      setSelectedTargetPeriod('');
      
      // Refresh data - don't let fetchData errors bubble up
      try {
        await fetchData();
      } catch (fetchError) {
        console.error('Failed to refresh data after convert:', fetchError);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal mengkonversi pembelian menjadi stok');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      branch_id: branchId || '',
      branch_account_id: '',
      supplier_name: '',
      purchase_date: new Date().toISOString().split('T')[0],
      partai: '',
      items: createDefaultRows()
    });
    setEditingId(null);
    setEditingConverted(false);
  };

  const getBranchName = (branchId) => {
    const branch = branches.find(b => b.id === branchId);
    return branch ? branch.name : 'Unknown';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount).replace('Rp', 'Rp.');
  };

  const formatNumber = (value) => {
    if (!value) return '';
    return new Intl.NumberFormat('id-ID').format(value);
  };

  const parseFormattedNumber = (value) => {
    if (!value) return 0;
    return parseFloat(value.toString().replace(/\./g, '')) || 0;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading && purchases.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Periode tertutup, atau nota "Stok Awal" yang sumbernya dari periode tertutup:
  // hanya partai & nama bahan yang boleh diubah.
  const lockedByClosedPeriod = !!editingId && (isClosedPeriod || (editingConverted && hasClosedPeriod));

  return (
    <div className="space-y-6" data-testid="purchases-section">
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-teal-600 hover:bg-teal-700" data-testid="add-purchase-btn">
                <Plus size={20} className="mr-2" />
                Tambah Pembelian
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-[95vw] max-h-[95vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Pembelian' : 'Tambah Pembelian Baru'}</DialogTitle>
            </DialogHeader>
            {lockedByClosedPeriod && (
              <div
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800"
                data-testid="closed-period-edit-notice"
              >
                Periode sumber sudah ditutup. Hanya <strong>Partai</strong> dan <strong>Nama Bahan</strong> yang dapat diubah.
              </div>
            )}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label htmlFor="supplier_name">Nama Supplier *</Label>
                  <Input
                    id="supplier_name"
                    data-testid="purchase-supplier-input"
                    value={formData.supplier_name}
                    onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })}
                    required
                    autoComplete="off"
                    readOnly={lockedByClosedPeriod}
                    className={lockedByClosedPeriod ? 'bg-slate-100 cursor-not-allowed' : ''}
                  />
                </div>
                
                <div>
                  <Label htmlFor="purchase_date">Tanggal Pembelian *</Label>
                  <Input
                    id="purchase_date"
                    data-testid="purchase-date-input"
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                    required
                    autoComplete="off"
                    readOnly={lockedByClosedPeriod}
                    className={lockedByClosedPeriod ? 'bg-slate-100 cursor-not-allowed' : ''}
                  />
                </div>

                <div>
                  <Label htmlFor="partai">Partai</Label>
                  <Input
                    id="partai"
                    data-testid="purchase-partai-input"
                    value={formData.partai}
                    onChange={(e) => setFormData({ ...formData, partai: e.target.value })}
                    autoComplete="off"
                  />
                </div>

                <div>
                  <Label htmlFor="account">Rekening *</Label>
                  <SearchableSelect
                    value={formData.branch_account_id}
                    onValueChange={(value) => setFormData({ ...formData, branch_account_id: value })}
                    options={(() => {
                      const accountOptions = branchAccounts.map(account => ({
                        value: account.id,
                        label: `${account.account_name} (Saldo: Rp.\u00a0${account.balance.toLocaleString('id-ID')})`
                      }));
                      // If editing and current account is not in options, add it as disabled option
                      if (formData.branch_account_id && !branchAccounts.find(acc => acc.id === formData.branch_account_id)) {
                        accountOptions.unshift({
                          value: formData.branch_account_id,
                          label: `⚠️ Rekening tidak ditemukan (ID: ${formData.branch_account_id})`
                        });
                      }
                      return accountOptions;
                    })()}
                    placeholder="Pilih rekening..."
                    searchPlaceholder="Cari rekening..."
                    required
                    disabled={lockedByClosedPeriod}
                  />
                  {formData.branch_account_id && !branchAccounts.find(acc => acc.id === formData.branch_account_id) && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠️ Rekening yang digunakan sebelumnya tidak ditemukan. Silakan pilih rekening yang valid.
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-base font-semibold">Daftar Bahan</Label>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddRow}
                    data-testid="add-row-btn"
                  >
                    <Plus size={16} className="mr-1" />
                    Tambah Row
                  </Button>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="w-[25%]">Nama Bahan</TableHead>
                        <TableHead className="w-[15%]">Berat (gr)</TableHead>
                        <TableHead className="w-[15%]">Harga/gr</TableHead>
                        <TableHead className="w-[15%]">Subtotal</TableHead>
                        <TableHead className="w-[25%]">Keterangan</TableHead>
                        <TableHead className="w-[5%]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index} data-testid={`item-row-${index}`}>
                          <TableCell className="p-2">
                            <Input
                              id={`item-${index}-0`}
                              value={item.name}
                              onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 0)}
                              data-testid={`item-name-${index}`}
                              className="h-9"
                              autoComplete="off"
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              id={`item-${index}-1`}
                              type="text"
                              value={item.weight_kg}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\./g, '');
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleItemChange(index, 'weight_kg', value ? formatNumber(parseFloat(value)) : '');
                                }
                              }}
                              onKeyDown={(e) => handleKeyDown(e, index, 1)}
                              data-testid={`item-weight-${index}`}
                              className={`h-9 text-right ${lockedByClosedPeriod ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                              autoComplete="off"
                              readOnly={lockedByClosedPeriod}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              id={`item-${index}-2`}
                              type="text"
                              value={item.price_per_kg}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\./g, '');
                                if (value === '' || /^\d+$/.test(value)) {
                                  handleItemChange(index, 'price_per_kg', value ? formatNumber(parseFloat(value)) : '');
                                }
                              }}
                              onKeyDown={(e) => handleKeyDown(e, index, 2)}
                              data-testid={`item-price-${index}`}
                              className={`h-9 text-right ${lockedByClosedPeriod ? 'bg-slate-100 cursor-not-allowed' : ''}`}
                              autoComplete="off"
                              readOnly={lockedByClosedPeriod}
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              id={`item-${index}-3`}
                              type="text"
                              value={item.subtotal}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\./g, '');
                                // Allow negative sign at the beginning
                                if (value === '' || value === '-' || /^-?\d+$/.test(value)) {
                                  handleItemChange(index, 'subtotal', value ? (value === '-' ? '-' : formatNumber(parseFloat(value))) : '');
                                }
                              }}
                              onKeyDown={(e) => handleKeyDown(e, index, 3)}
                              readOnly={lockedByClosedPeriod || (item.weight_kg && item.price_per_kg)}
                              className={`h-9 font-semibold text-orange-600 text-right ${lockedByClosedPeriod || (item.weight_kg && item.price_per_kg) ? 'bg-slate-100 cursor-not-allowed' : 'bg-amber-50'}`}
                              data-testid={`item-subtotal-${index}`}
                              autoComplete="off"
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Input
                              id={`item-${index}-4`}
                              value={item.keterangan || ''}
                              onChange={(e) => handleItemChange(index, 'keterangan', e.target.value)}
                              onKeyDown={(e) => handleKeyDown(e, index, 4)}
                              data-testid={`item-keterangan-${index}`}
                              className="h-9"
                              autoComplete="off"
                            />
                          </TableCell>
                          <TableCell className="p-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveItem(index)}
                              className="text-red-600 hover:text-red-700 h-9 w-9 p-0"
                              data-testid={`remove-item-${index}`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      {/* Total Row */}
                      <TableRow className="bg-teal-50 border-t-2 border-teal-600">
                        <TableCell className="p-2 font-bold text-teal-800">Total</TableCell>
                        <TableCell className="p-2 font-bold text-teal-800 text-right">
                          {formatNumber(formData.items.reduce((sum, item) => sum + parseFormattedNumber(item.weight_kg), 0))} gr
                        </TableCell>
                        <TableCell className="p-2"></TableCell>
                        <TableCell className="p-2 font-bold text-teal-800 text-right">
                          {formatCurrency(formData.items.reduce((sum, item) => sum + parseFormattedNumber(item.subtotal), 0))}
                        </TableCell>
                        <TableCell className="p-2" colSpan="2"></TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-end mt-3 pt-3 border-t">
                  <div className="text-right">
                    <p className="text-sm text-slate-600">Total Pembelian</p>
                    <p className="text-xl font-bold text-teal-600">
                      {formatCurrency(formData.items.reduce((sum, item) => sum + parseFormattedNumber(item.subtotal), 0))}
                    </p>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex justify-between items-center">
                <div>
                  {editingId && (
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDeleteClick(editingId);
                      }}
                      disabled={loading}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      <Trash2 size={16} className="mr-2" />
                      Hapus
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Batal</Button>
                  <Button type="submit" className="bg-teal-600 hover:bg-teal-700" disabled={loading} data-testid="purchase-submit-btn">
                    {loading ? 'Menyimpan...' : 'Simpan'}
                  </Button>
                </div>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        
        {selectedPurchases.length > 0 && (
          <>
            <Button 
              variant="outline" 
              onClick={() => setBulkEditDialogOpen(true)}
              data-testid="bulk-edit-btn"
            >
              Edit Partai ({selectedPurchases.length})
            </Button>
            <Button 
              variant="outline"
              onClick={() => setMoveDialogOpen(true)}
              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
              data-testid="move-to-period-btn"
            >
              <ArrowRightLeft size={16} className="mr-2" />
              Pindahkan ke Periode Lain ({selectedPurchases.length})
            </Button>
            <Button 
              variant="outline"
              onClick={() => setConvertDialogOpen(true)}
              className="bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-300"
              data-testid="convert-to-stock-btn"
            >
              <Package size={16} className="mr-2" />
              Jadikan Stok ke Periode Lain ({selectedPurchases.length})
            </Button>
          </>
        )}
      </div>
      </div>
      
      {/* Bulk Edit Partai Dialog */}
      <Dialog open={bulkEditDialogOpen} onOpenChange={setBulkEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Partai Massal</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Mengubah partai untuk {selectedPurchases.length} pembelian yang dipilih
            </p>
            <div>
              <Label htmlFor="bulk_partai">Partai Baru *</Label>
              <Input
                id="bulk_partai"
                value={bulkPartai}
                onChange={(e) => setBulkPartai(e.target.value)}
                
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkEditDialogOpen(false)}>
              Batal
            </Button>
            <Button 
              onClick={handleBulkEditPartai} 
              className="bg-teal-600 hover:bg-teal-700"
              disabled={loading}
            >
              {loading ? 'Memperbarui...' : 'Perbarui'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Konfirmasi Hapus</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">Yakin ingin menghapus pembelian ini? Saldo akan dikembalikan ke rekening. Tindakan ini tidak dapat dibatalkan.</p>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setDeleteDialogOpen(false);
                setDeletingId(null);
              }}
            >
              Batal
            </Button>
            <Button 
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Menghapus...' : 'Ya, Hapus'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Move to Period Dialog */}
      <Dialog open={moveDialogOpen} onOpenChange={setMoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <ArrowRightLeft size={20} />
              Pindahkan ke Periode Lain
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Memindahkan {selectedPurchases.length} pembelian yang dipilih ke periode lain. 
              Pembelian akan dihapus dari periode saat ini dan total rupiah akan dicatat sebagai &quot;Pindah Periode&quot; di tabel asset.
            </p>
            <div>
              <Label htmlFor="move_target_period">Pilih Periode Tujuan (Hanya Periode Aktif) *</Label>
              <Select value={selectedTargetPeriod} onValueChange={setSelectedTargetPeriod}>
                <SelectTrigger id="move_target_period">
                  <SelectValue placeholder="Pilih periode..." />
                </SelectTrigger>
                <SelectContent>
                  {activePeriods.length === 0 ? (
                    <SelectItem value="none" disabled>Tidak ada periode aktif lain</SelectItem>
                  ) : (
                    activePeriods.map(period => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setMoveDialogOpen(false);
              setSelectedTargetPeriod('');
            }}>
              Batal
            </Button>
            <Button 
              onClick={handleMoveToPeriod} 
              className="bg-blue-600 hover:bg-blue-700"
              disabled={loading || !selectedTargetPeriod}
            >
              {loading ? 'Memindahkan...' : 'Pindahkan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Convert to Stock Dialog */}
      <Dialog open={convertDialogOpen} onOpenChange={setConvertDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Package size={20} />
              Jadikan Stok ke Periode Lain
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Menjadikan {selectedPurchases.length} pembelian yang dipilih sebagai stok di periode lain. 
              Pembelian tetap ada di periode saat ini, data akan dimasukkan ke Penjualan dengan keterangan &quot;Stok&quot; dan akan muncul sebagai &quot;Persediaan&quot; di tabel asset.
            </p>
            <div>
              <Label htmlFor="convert_target_period">Pilih Periode Tujuan (Hanya Periode Aktif) *</Label>
              <Select value={selectedTargetPeriod} onValueChange={setSelectedTargetPeriod}>
                <SelectTrigger id="convert_target_period">
                  <SelectValue placeholder="Pilih periode..." />
                </SelectTrigger>
                <SelectContent>
                  {activePeriods.length === 0 ? (
                    <SelectItem value="none" disabled>Tidak ada periode aktif lain</SelectItem>
                  ) : (
                    activePeriods.map(period => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => {
              setConvertDialogOpen(false);
              setSelectedTargetPeriod('');
            }}>
              Batal
            </Button>
            <Button 
              onClick={handleConvertToStock} 
              className="bg-purple-600 hover:bg-purple-700"
              disabled={loading || !selectedTargetPeriod}
            >
              {loading ? 'Memproses...' : 'Jadikan Stok'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {!activePeriod && (
        <Card className="border-l-4 border-l-yellow-500 bg-yellow-50">
          <CardContent className="pt-6">
            <p className="text-yellow-800">Tidak ada periode aktif. Buat periode baru di menu Periode untuk mulai mencatat transaksi.</p>
          </CardContent>
        </Card>
      )}

      {/* Tabel 1: Pembelian Normal (Belum Dijadikan Stok) */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShoppingCart size={20} />
            Pembelian Aktif : {getBranchName(branchId)}
            <span className="text-sm font-normal text-slate-500">({normalPurchases.length} pembelian)</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {normalPurchases.length === 0 ? (
            <div className="py-12 text-center">
              <ShoppingCart size={48} className="mx-auto text-slate-300 mb-4" />
              <p className="text-slate-600">Belum ada data pembelian aktif.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedPurchases.length === normalPurchases.length && normalPurchases.length > 0}
                        onCheckedChange={toggleSelectAll}
                      />
                    </TableHead>
                    <TableHead className="w-[80px]">Tanggal</TableHead>
                    <TableHead className="w-[120px]">Supplier</TableHead>
                    <TableHead className="w-[80px]">Partai</TableHead>
                    <TableHead className="w-[150px]">Nama Bahan</TableHead>
                    <TableHead className="w-[80px] text-right">Berat (gr)</TableHead>
                    <TableHead className="w-[100px] text-right">Harga/gr</TableHead>
                    <TableHead className="w-[100px] text-right">Subtotal</TableHead>
                    <TableHead className="w-[150px]">Keterangan</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Group purchases by partai
                    const groupedByPartai = normalPurchases.reduce((acc, purchase) => {
                      const partai = purchase.partai || 'Tanpa Partai';
                      if (!acc[partai]) acc[partai] = [];
                      acc[partai].push(purchase);
                      return acc;
                    }, {});

                    return Object.entries(groupedByPartai).map(([partai, partaiPurchases]) => (
                      <React.Fragment key={partai}>
                        {/* Partai Header */}
                        <TableRow className="bg-teal-50 border-t-2 border-teal-300">
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={partaiPurchases.every(p => selectedPurchases.includes(p.id))}
                              onCheckedChange={() => togglePartaiPurchases(partaiPurchases.map(p => p.id))}
                              data-testid={`select-partai-purchases-${partai}`}
                            />
                          </TableCell>
                          <TableCell colSpan={8} className="font-bold text-teal-700">
                            <div className="grid grid-cols-3 items-center">
                              <span className="justify-self-start">PARTAI: {partai} ({partaiPurchases.length} pembelian)</span>
                              <span className="justify-self-center text-blue-600 font-bold text-lg">{getBranchName(partaiPurchases[0]?.branch_id)}</span>
                              <span></span>
                            </div>
                          </TableCell>
                        </TableRow>
                        
                        {/* Purchases in this partai */}
                        {partaiPurchases.map((purchase) => (
                          <React.Fragment key={purchase.id}>
                            {purchase.items && purchase.items.length > 0 ? (
                              purchase.items.map((item, idx) => (
                                <TableRow 
                                  key={`${purchase.id}-${idx}`} 
                                  data-testid={`purchase-item-${purchase.id}-${idx}`}
                                  className="cursor-pointer hover:bg-blue-50 transition-colors"
                                  onClick={(e) => {
                                    // Don't trigger if clicking checkbox
                                    if (e.target.type !== 'checkbox') {
                                      handleEdit(purchase);
                                    }
                                  }}
                                >
                                  {idx === 0 ? (
                                    <>
                                      <TableCell 
                                        rowSpan={purchase.items.length} 
                                        className="border-r align-top"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Checkbox
                                          checked={selectedPurchases.includes(purchase.id)}
                                          onCheckedChange={() => toggleSelectPurchase(purchase.id)}
                                        />
                                      </TableCell>
                                      <TableCell rowSpan={purchase.items.length} className="border-r align-top">
                                        {formatDate(purchase.purchase_date)}
                                      </TableCell>
                                      <TableCell rowSpan={purchase.items.length} className="border-r align-top">
                                        {purchase.supplier_name}
                                      </TableCell>
                                      <TableCell rowSpan={purchase.items.length} className="border-r align-top">
                                        {purchase.partai || '-'}
                                      </TableCell>
                                    </>
                                  ) : null}
                                  <TableCell>{item.name || '-'}</TableCell>
                                  <TableCell className="text-right">
                                    {formatNumber(item.weight_kg)}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCurrency(item.price_per_kg)}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-orange-600">
                                    {formatCurrency(item.subtotal)}
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-600">
                                    {item.keterangan ? (
                                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                        item.keterangan === 'Stok Awal' 
                                          ? 'bg-green-100 text-green-800' 
                                          : 'bg-slate-100 text-slate-800'
                                      }`}>
                                        {item.keterangan}
                                      </span>
                                    ) : '-'}
                                  </TableCell>
                                </TableRow>
                              ))
                            ) : (
                              <TableRow key={purchase.id}>
                                <TableCell>
                                  <Checkbox
                                    checked={selectedPurchases.includes(purchase.id)}
                                    onCheckedChange={() => toggleSelectPurchase(purchase.id)}
                                  />
                                </TableCell>
                                <TableCell>{formatDate(purchase.purchase_date)}</TableCell>
                                <TableCell>{purchase.supplier_name}</TableCell>
                                <TableCell>{purchase.partai || '-'}</TableCell>
                                <TableCell 
                                  colSpan={4} 
                                  className="text-center text-slate-400 cursor-pointer hover:bg-blue-50"
                                  onClick={() => handleEdit(purchase)}
                                >
                                  Tidak ada item
                                </TableCell>
                              </TableRow>
                            )}
                          </React.Fragment>
                        ))}
                        
                        {/* Subtotal per Partai */}
                        <TableRow className="bg-slate-50 font-semibold">
                          <TableCell colSpan={5} className="text-right">Subtotal {partai}</TableCell>
                          <TableCell className="text-right">
                            {formatNumber(
                              partaiPurchases.reduce((sum, p) => 
                                sum + (p.items?.reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0) || 0), 0
                              )
                            )}
                          </TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right text-orange-600">
                            {formatCurrency(
                              partaiPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0)
                            )}
                          </TableCell>
                          <TableCell></TableCell>
                        </TableRow>
                      </React.Fragment>
                    ));
                  })()}
                  {/* Total Row */}
                  {purchases.length > 0 && (
                    <TableRow className="bg-slate-100 font-bold border-t-2">
                      <TableCell colSpan={5} className="text-right">TOTAL</TableCell>
                      <TableCell className="text-right">
                        {formatNumber(
                          purchases.reduce((sum, p) => 
                            sum + (p.items?.reduce((itemSum, item) => itemSum + (item.weight_kg || 0), 0) || 0), 0
                          )
                        )}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell className="text-right text-orange-600">
                        {formatCurrency(
                          normalPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0)
                        )}
                      </TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabel 2: Pembelian yang Sudah Dijadikan Stok */}
      {convertedPurchases.length > 0 && (
        <Card className="mt-6">
          <CardHeader className="bg-purple-50">
            <CardTitle className="flex items-center gap-2 text-purple-700">
              <Package size={20} />
              Pembelian Dijadikan Stok
              <span className="text-sm font-normal">({convertedPurchases.length} pembelian)</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table className="text-xs">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[80px]">Tanggal</TableHead>
                    <TableHead className="w-[120px]">Supplier</TableHead>
                    <TableHead className="w-[80px]">Partai</TableHead>
                    <TableHead className="w-[150px]">Nama Bahan</TableHead>
                    <TableHead className="w-[80px] text-right">Berat (gr)</TableHead>
                    <TableHead className="w-[100px] text-right">Harga/gr</TableHead>
                    <TableHead className="w-[100px] text-right">Subtotal</TableHead>
                    <TableHead className="w-[120px]">Keterangan</TableHead>
                    <TableHead className="w-[80px] text-center">Aksi</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    // Group converted purchases by partai
                    const groupedByPartai = convertedPurchases.reduce((acc, purchase) => {
                      const partai = purchase.partai || 'Tanpa Partai';
                      if (!acc[partai]) acc[partai] = [];
                      acc[partai].push(purchase);
                      return acc;
                    }, {});

                    return Object.entries(groupedByPartai).map(([partai, partaiPurchases]) => (
                      <React.Fragment key={partai}>
                        {/* Partai Header */}
                        <TableRow className="bg-purple-50 border-t-2 border-purple-300">
                          <TableCell colSpan={9} className="font-bold text-purple-700">
                            PARTAI: {partai} ({partaiPurchases.length} pembelian)
                          </TableCell>
                        </TableRow>
                        
                        {/* Purchases in this partai */}
                        {partaiPurchases.map((purchase) => (
                          <React.Fragment key={purchase.id}>
                            {purchase.items && purchase.items.length > 0 ? (
                              purchase.items.map((item, idx) => (
                                <TableRow 
                                  key={`${purchase.id}-${idx}`}
                                  className="opacity-75"
                                >
                                  {idx === 0 ? (
                                    <>
                                      <TableCell rowSpan={purchase.items.length} className="border-r align-top">
                                        {formatDate(purchase.purchase_date)}
                                      </TableCell>
                                      <TableCell rowSpan={purchase.items.length} className="border-r align-top">
                                        {purchase.supplier_name}
                                      </TableCell>
                                      <TableCell rowSpan={purchase.items.length} className="border-r align-top">
                                        {purchase.partai || '-'}
                                      </TableCell>
                                    </>
                                  ) : null}
                                  <TableCell>{item.name || '-'}</TableCell>
                                  <TableCell className="text-right">
                                    {formatNumber(item.weight_kg)}
                                  </TableCell>
                                  <TableCell className="text-right">
                                    {formatCurrency(item.price_per_kg)}
                                  </TableCell>
                                  <TableCell className="text-right font-semibold text-purple-600">
                                    {formatCurrency(item.subtotal)}
                                  </TableCell>
                                  <TableCell className="text-xs text-slate-600">
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                      Transfer Stok
                                    </span>
                                  </TableCell>
                                  {idx === 0 ? (
                                    <TableCell rowSpan={purchase.items.length} className="text-center align-top">
                                      <Button
                                        onClick={async () => {
                                          if (!confirm('Yakin ingin membatalkan konversi stok ini? Sales dan pembelian tujuan akan dihapus.')) {
                                            return;
                                          }
                                          try {
                                            await api.post('/purchases/cancel-convert-to-stock', {
                                              purchase_ids: [purchase.id]
                                            });
                                            toast.success('Konversi stok berhasil dibatalkan');
                                            fetchData();
                                          } catch (error) {
                                            console.error('Failed to cancel conversion:', error);
                                            toast.error('Gagal membatalkan: ' + (error.response?.data?.detail || error.message));
                                          }
                                        }}
                                        variant="outline"
                                        size="sm"
                                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                      >
                                        Batalkan
                                      </Button>
                                    </TableCell>
                                  ) : null}
                                </TableRow>
                              ))
                            ) : null}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    ));
                  })()}
                  
                  {/* Total Row */}
                  {convertedPurchases.length > 0 && (
                    <TableRow className="bg-purple-50 border-t-2 border-purple-600">
                      <TableCell colSpan={6} className="font-bold text-purple-800">
                        Total
                      </TableCell>
                      <TableCell className="font-bold text-purple-800 text-right">
                        {formatCurrency(
                          convertedPurchases.reduce((sum, p) => sum + (p.total_amount || 0), 0)
                        )}
                      </TableCell>
                      <TableCell colSpan={2}></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Purchases;
